import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import all locale files
import en from './locales/en.json';
import es from './locales/es.json';
import ar from './locales/ar.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import ru from './locales/ru.json';
import zh from './locales/zh.json';
import tr from './locales/tr.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';

const LANGUAGE_STORAGE_KEY = 'app_language';

// Language configuration
export const SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English', nativeName: 'English', rtl: false },
    { code: 'es', name: 'Spanish', nativeName: 'Español', rtl: false },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
    { code: 'fr', name: 'French', nativeName: 'Français', rtl: false },
    { code: 'de', name: 'German', nativeName: 'Deutsch', rtl: false },
    { code: 'pt', name: 'Portuguese', nativeName: 'Português', rtl: false },
    { code: 'ru', name: 'Russian', nativeName: 'Русский', rtl: false },
    { code: 'zh', name: 'Chinese', nativeName: '简体中文', rtl: false },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', rtl: false },
    { code: 'it', name: 'Italian', nativeName: 'Italiano', rtl: false },
    { code: 'ja', name: 'Japanese', nativeName: '日本語', rtl: false },
    { code: 'ko', name: 'Korean', nativeName: '한국어', rtl: false },
];

// Resources for i18next
const resources = {
    en: { translation: en },
    es: { translation: es },
    ar: { translation: ar },
    fr: { translation: fr },
    de: { translation: de },
    pt: { translation: pt },
    ru: { translation: ru },
    zh: { translation: zh },
    tr: { translation: tr },
    it: { translation: it },
    ja: { translation: ja },
    ko: { translation: ko },
};

// Get saved language from localStorage
function getSavedLanguage() {
    try {
        const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) {
            return saved;
        }
    } catch (e) {
        console.warn('[i18n] Failed to load saved language:', e);
    }
    return 'en';
}

// Initialize i18next with saved language
i18n
    .use(initReactI18next)
    .init({
        resources,
        lng: getSavedLanguage(), // Load saved language or default to English
        fallbackLng: 'en',
        interpolation: {
            escapeValue: false, // React already escapes values
        },
        react: {
            useSuspense: false,
        },
    });

/**
 * Change the app language and persist the choice
 * @param languageCode - The language code to switch to
 */
export function changeLanguage(languageCode) {
    const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === languageCode);

    // Keep LTR layout for all languages (matching mobile app behavior)
    // Arabic text will still display correctly, just layout stays LTR
    if (langConfig) {
        document.documentElement.dir = 'ltr'; // Always LTR like mobile app
        document.documentElement.lang = languageCode;
    }

    // Persist the language choice
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
    } catch (e) {
        console.warn('[i18n] Failed to save language preference:', e);
    }

    return i18n.changeLanguage(languageCode);
}

/**
 * Get the current language code
 */
export function getCurrentLanguage() {
    return i18n.language || 'en';
}

/**
 * Get language display name by code
 */
export function getLanguageDisplayName(code) {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang?.nativeName || code;
}

/**
 * Check if the current language is RTL
 */
export function isCurrentLanguageRTL() {
    const currentLang = getCurrentLanguage();
    const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === currentLang);
    return langConfig?.rtl || false;
}

export default i18n;
